// The join URL a host surface encodes, and the encoder shape it takes as an injected input
// (`tournament-big-screen` design **D14**). Mirrors `test/fake-qrcode.ts`'s G-6 harness
// field-for-field: that fake is what production code is tested against, so the two must never
// diverge in shape.
//
// The composition root is the only place that constructs a real encoder — `HostGathering`
// itself never imports the `qrcode` package, which is what keeps "renders from injected inputs
// alone" true of it.

/** The encoder shape a host surface takes as an injected input — `qrcode`'s `toDataURL`, and
 * nothing wider. */
export type QrEncoder = (text: string) => Promise<string>;

/** A join code is opaque text and goes in the query string percent-encoded; the origin comes
 * from the caller because a projected QR has to point at the address the room can actually
 * reach, not at whatever the browser happens to be showing. */
export function joinUrl(origin: string, joinCode: string): string {
  const url = new URL('/', origin);
  url.searchParams.set('join', joinCode);
  return url.toString();
}

/** The `join` parameter's shape, as a **trust boundary** check rather than a format assertion.
 *
 * What arrives here is a scanned URL — the one input to this app nobody typed and nobody chose the
 * origin of — so it is bounded and screened before it is allowed to seed a form field: printable
 * ASCII, no whitespace, no control characters, and short enough that it cannot be a payload. The
 * exact code format stays the server's business, which refuses a wrong one with the same typed
 * refusal it gives a mistyped one. */
const JOIN_PARAM = /^[\x21-\x7e]{1,128}$/;

/**
 * **Reads** the `join` query parameter the projected QR encodes, validated — or `null` when there
 * is nothing usable. Strips nothing.
 *
 * The QR has always encoded `/?join=<code>`, and until this nothing read it: scanning the code the
 * room is looking at opened an empty join form, which is the onboarding path the whole host
 * gathering surface exists to serve.
 *
 * **Pure, deliberately.** This used to be one `takeJoinParam` that read *and* stripped, called from
 * a lazy `useState` initialiser — and React re-invokes initialisers on the double render StrictMode
 * performs in development, which is how the app mounts. The first invocation captured the code and
 * removed the parameter; the second found nothing and returned `null`, and the second is the value
 * React keeps. The seed was captured and immediately thrown away, so every development rehearsal of
 * the scan landed on an empty form. Reading is therefore idempotent, and consuming is
 * {@link stripJoinParam}'s job, from an effect.
 */
export function readJoinParam(location: Location): string | null {
  const raw = new URL(location.href).searchParams.get('join');
  if (raw === null) return null;
  const code = raw.trim();
  return JOIN_PARAM.test(code) ? code : null;
}

/**
 * **Consumes** the `join` parameter: removes it from the address bar, by `replaceState` rather than
 * a navigation. A join code is a secret and does not belong in a bookmark, a shared link or a
 * back-button entry, and a parameter that survived would re-seed the field on every later render of
 * Home, overwriting whatever the player had since typed. Only `join` is removed — any other
 * parameter is somebody else's and is left exactly where it was.
 *
 * Idempotent: an address with no `join` parameter is left untouched, so the second run of a
 * StrictMode-double-invoked effect writes no history entry.
 */
export function stripJoinParam(location: Location, history: History): void {
  const url = new URL(location.href);
  if (!url.searchParams.has('join')) return;
  url.searchParams.delete('join');
  history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
}
